import { useEffect, useMemo, useState } from "react";
import { Button, Empty, Input, InputNumber, Modal, Pagination, Select, Spin, Tag, message } from "antd";
import PageLayout from "@layouts/PageLayout";
import { useAppSelector } from "@store/hooks";
import { cancelOrder, formatInr, getAllOrders, getMyOrders, requestReturn, updateOrderAddress, updateOrderStatus, updateRefundStatus } from "@services/shop.service";
import { PAGE_SIZE, matchesQuery } from "./list";
import OrderJourney from "./OrderJourney";
import "./shop.css";

const statusOptions = [
  { value: "order_received", label: "Order received" },
  { value: "packed", label: "Order packed" },
  { value: "shipped", label: "Order shipped" },
  { value: "in_transit", label: "In transit" },
  { value: "out_for_delivery", label: "Out for delivery" },
  { value: "delivered", label: "Delivered" },
  { value: "delivery_failed", label: "Delivery failed" },
  { value: "return_requested", label: "Return requested" },
  { value: "return_in_transit", label: "Return in transit" },
  { value: "returned", label: "Returned to warehouse" },
  { value: "cancelled", label: "Cancelled" },
];

const refundOptions = [
  { value: "eligible", label: "Refund eligible" },
  { value: "initiated", label: "Refund initiated" },
  { value: "processing", label: "Refund processing" },
  { value: "partially_refunded", label: "Partially refunded" },
  { value: "refunded", label: "Refunded" },
  { value: "not_received", label: "Refund not received" },
  { value: "failed", label: "Refund failed" },
];

const refundLabel = (status?: string) => refundOptions.find((option) => option.value === status)?.label || "No refund";

const color: Record<string, string> = {
  paid: "green",
  order_received: "green",
  pending: "gold",
  packed: "blue",
  shipped: "blue",
  in_transit: "blue",
  out_for_delivery: "blue",
  delivered: "green",
  failed: "red",
  cancelled: "default",
  refunded: "green",
  return_requested: "orange",
};

function OrdersPage() {
  const role = useAppSelector((state) => state.auth.user?.role || "user");
  const isAdmin = role === "admin" || role === "superadmin";
  const [orders, setOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [action, setAction] = useState<{ id: string; kind: "cancel" | "return" | "items"; items?: { item_id: string; quantity: number }[] } | null>(null);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [updatingId, setUpdatingId] = useState("");
  const [lineQty, setLineQty] = useState<Record<string, number>>({});
  const [addressOrder, setAddressOrder] = useState<any>(null);

  const load = () => {
    setLoading(true);
    const request = isAdmin ? getAllOrders() : getMyOrders();
    request?.then((data) => setOrders(Array.isArray(data) ? data : [])).finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, [isAdmin]);

  const filtered = useMemo(
    () =>
      orders.filter((order) =>
        matchesQuery(query, [
          order.order_number,
          order.status,
          order.status_label,
          order.shipping_name,
          order.id,
          order.tracking_id,
          ...(order.items || []).map((item: any) => item.name),
        ]),
      ),
    [orders, query],
  );
  const pages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const current = Math.min(page, pages);
  const visible = filtered.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);

  const submitAction = async () => {
    if (!action) return;
    setSaving(true);
    try {
      const call = action.kind === "return" ? requestReturn : cancelOrder;
      await call(action.id, action.kind === "items" ? { reason, items: action.items } : { reason });
      message.success(action.kind === "return" ? "Return requested" : action.kind === "items" ? "Items cancelled" : "Order cancelled");
      setAction(null);
      setReason("");
      load();
    } finally {
      setSaving(false);
    }
  };

  const changeStatus = async (order: any, status: string) => {
    if (status === order.status) return;
    if (status === "cancelled") {
      setAction({ id: order.id, kind: "cancel" });
      setReason("");
      return;
    }
    const next = (order.actions?.next_statuses || []).some((step: any) => step.status === status);
    setUpdatingId(order.id);
    try {
      await updateOrderStatus(order.id, {
        status,
        ...(next ? {} : { force: true, note: "Status updated by admin" }),
      });
      message.success("Status updated");
      load();
    } finally {
      setUpdatingId("");
    }
  };

  const changeRefund = async (order: any, status: string) => {
    setUpdatingId(order.id);
    try {
      await updateRefundStatus(order.id, {
        status,
        note: status === "not_received" ? "Customer did not receive the refund" : undefined,
      });
      message.success("Refund status updated");
      load();
    } finally {
      setUpdatingId("");
    }
  };

  const saveAddress = async () => {
    if (!addressOrder) return;
    setSaving(true);
    try {
      await updateOrderAddress(addressOrder.id, addressOrder);
      message.success("Address updated");
      setAddressOrder(null);
      load();
    } finally {
      setSaving(false);
    }
  };

  return (
    <PageLayout>
      <div className="shop-page">
        <div className="shop-hero">
          <div>
            <h2>Orders</h2>
            <p>
              {orders.length
                ? `${orders.length} ${isAdmin ? "in the shop" : "placed"}`
                : isAdmin
                  ? "Orders placed in the shop appear here."
                  : "Orders you pay for appear here."}
            </p>
          </div>
          <Input.Search
            allowClear
            placeholder="Search product or status"
            style={{ width: "min(100%, 280px)" }}
            onChange={(event) => {
              setQuery(event.target.value);
              setPage(1);
            }}
          />
        </div>
        {loading ? (
          <div className="surface" style={{ textAlign: "center", padding: 48 }}><Spin /></div>
        ) : !visible.length ? (
          <div className="surface">
            <Empty description={query.trim() ? "No orders match that search" : "No orders yet"} />
          </div>
        ) : (
          <>
            {visible.map((order) => (
              <article className="order-card" key={order.id}>
                <div className="order-top">
                  <div>
                    <strong>Order ID: {order.order_number || "Order"}</strong>
                    <div>{formatInr(order.total)}</div>
                    <div className="muted">{order.created_at ? new Date(order.created_at).toLocaleString() : ""}</div>
                    <div className="muted">{[order.shipping_name, order.address].filter(Boolean).join(" · ")}</div>
                    {order.tracking_id ? (
                      <div className="muted">{[order.carrier, order.tracking_id].filter(Boolean).join(" · ")}</div>
                    ) : null}
                  </div>
                  {isAdmin ? (
                    <Select
                      value={statusOptions.some((option) => option.value === order.status) ? order.status : undefined}
                      placeholder={order.status_label || "Change status"}
                      style={{ width: 220 }}
                      loading={updatingId === order.id}
                      options={statusOptions}
                      onChange={(status) => changeStatus(order, status)}
                    />
                  ) : (
                    <Tag color={color[order.status] || "default"}>{order.status_label || order.status || "pending"}</Tag>
                  )}
                </div>
                <div style={{ marginTop: 12 }}>
                  {(order.items || []).length ? (
                    order.items.map((item: any) => {
                      const active = item.active_quantity ?? item.quantity;
                      return (
                        <div key={item.id} className="order-item-row">
                          <span className="muted">
                            {active} of {item.quantity} × {item.name} · {formatInr(item.price)}
                            {item.cancelled_quantity ? ` · ${item.cancelled_quantity} cancelled` : ""}
                          </span>
                          {order.actions?.can_cancel_items && active > 0 ? (
                            <InputNumber
                              min={0}
                              max={active}
                              value={lineQty[item.id] || 0}
                              onChange={(value) => setLineQty((current) => ({ ...current, [item.id]: Number(value || 0) }))}
                            />
                          ) : null}
                        </div>
                      );
                    })
                  ) : (
                    <div className="muted">No items recorded</div>
                  )}
                </div>
                <OrderJourney order={order} />
                <div className="order-actions">
                  {order.actions?.can_cancel ? (
                    <Button onClick={() => { setAction({ id: order.id, kind: "cancel" }); setReason(""); }}>
                      Cancel order
                    </Button>
                  ) : order.actions?.cancel_block_reason && !order.actions?.can_cancel_items ? (
                    <p className="muted">{order.actions.cancel_block_reason}</p>
                  ) : null}
                  {order.actions?.can_cancel_items ? (
                    <Button
                      onClick={() => {
                        const items = (order.items || [])
                          .map((item: any) => ({ item_id: item.id, quantity: Number(lineQty[item.id] || 0) }))
                          .filter((item: any) => item.quantity > 0);
                        if (!items.length) {
                          message.warning("Choose how many units to cancel");
                          return;
                        }
                        setAction({ id: order.id, kind: "items", items });
                        setReason("");
                      }}
                    >
                      Cancel selected items
                    </Button>
                  ) : null}
                  {order.actions?.can_request_return ? (
                    <Button onClick={() => { setAction({ id: order.id, kind: "return" }); setReason(""); }}>
                      Request return
                    </Button>
                  ) : order.status === "delivered" && order.actions?.return_block_reason ? (
                    <p className="muted">{order.actions.return_block_reason}</p>
                  ) : null}
                  {order.actions?.can_change_address ? (
                    <Button
                      onClick={() =>
                        setAddressOrder({
                          id: order.id,
                          shipping_name: order.shipping_name || "",
                          phone: order.phone || "",
                          address: order.address || "",
                          city: order.city || "",
                          pincode: order.pincode || "",
                        })
                      }
                    >
                      Change address
                    </Button>
                  ) : null}
                  {isAdmin && order.actions?.can_set_refund_status ? (
                    <Select
                      value={order.refund_status && order.refund_status !== "none" ? order.refund_status : undefined}
                      placeholder="Refund status"
                      style={{ width: 200 }}
                      loading={updatingId === order.id}
                      options={refundOptions}
                      onChange={(status) => changeRefund(order, status)}
                    />
                  ) : order.refund_status && order.refund_status !== "none" ? (
                    <Tag>{refundLabel(order.refund_status)}</Tag>
                  ) : null}
                </div>
              </article>
            ))}
            <div className="list-footer">
              <Pagination
                current={current}
                pageSize={PAGE_SIZE}
                total={filtered.length}
                hideOnSinglePage
                showSizeChanger={false}
                onChange={setPage}
              />
            </div>
          </>
        )}
        <Modal
          title={action?.kind === "return" ? "Request a return" : action?.kind === "items" ? "Cancel selected items" : "Cancel this order"}
          open={Boolean(action)}
          okText={action?.kind === "return" ? "Request return" : action?.kind === "items" ? "Cancel items" : "Cancel order"}
          confirmLoading={saving}
          onCancel={() => setAction(null)}
          onOk={submitAction}
          okButtonProps={{ disabled: reason.trim().length < 3 }}
        >
          <Input.TextArea
            rows={3}
            value={reason}
            placeholder="Tell us why"
            onChange={(event) => setReason(event.target.value)}
          />
        </Modal>
        <Modal
          title="Change delivery address"
          open={Boolean(addressOrder)}
          confirmLoading={saving}
          onCancel={() => setAddressOrder(null)}
          onOk={saveAddress}
          okButtonProps={{
            disabled: !addressOrder?.shipping_name?.trim() || !addressOrder?.phone?.trim() || !addressOrder?.address?.trim(),
          }}
        >
          <div style={{ display: "grid", gap: 8 }}>
            <Input
              placeholder="Name"
              value={addressOrder?.shipping_name}
              onChange={(event) => setAddressOrder((current: any) => ({ ...current, shipping_name: event.target.value }))}
            />
            <Input
              placeholder="Phone"
              value={addressOrder?.phone}
              onChange={(event) => setAddressOrder((current: any) => ({ ...current, phone: event.target.value }))}
            />
            <Input.TextArea
              rows={2}
              placeholder="Address"
              value={addressOrder?.address}
              onChange={(event) => setAddressOrder((current: any) => ({ ...current, address: event.target.value }))}
            />
            <Input
              placeholder="City"
              value={addressOrder?.city}
              onChange={(event) => setAddressOrder((current: any) => ({ ...current, city: event.target.value }))}
            />
            <Input
              placeholder="Pincode"
              value={addressOrder?.pincode}
              onChange={(event) => setAddressOrder((current: any) => ({ ...current, pincode: event.target.value }))}
            />
          </div>
        </Modal>
      </div>
    </PageLayout>
  );
}

export default OrdersPage;
